import classnames from 'classnames';
import { forwardRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { UncontrolledTooltip } from 'reactstrap';
// Bypasses the shared/ui barrel: routing through it would create an import cycle, since the
// barrel also re-exports FabricJS/AIECGViewer, which imports @/entities/ecg (which this file
// transitively imports).
import IconButton from '@/shared/ui/components/Buttons/iconButton';
import {
  ZoomIn as iconZoomIn,
  ZoomOut as iconZoomOut,
  ZoomReset as iconZoomReset,
} from '@/shared/ui/icons';
import { ZOOM_SCALE_DEFAULT } from '../lib/chartHrHelper';
import useChartHR from '../model/useChartHR';
import './styles.scss';

export type ChartHRHandle = {
  handleClickArrowButton: (isNext: boolean) => void;
  resetCurrentSelect: () => void;
};

export type ChartHRProps = {
  keyRecoil: string;
  updatedSelectedStrip?: any;
  idHtmlChart: string;
  dataChart: any;
  timezoneOffset: number;
  isOldUI?: boolean;
  isDailyHrSummary?: boolean;
  minWidth?: number;
};

/**
 * Renders the heart rate chart of a report tab together with its zoom controls. Shows a skeleton while the chart or its options load, mounts the chart into the host element identified by the hook, and once rendering finishes exposes zoom in, zoom out and reset zoom buttons plus the current zoom percentage.
 *
 * @param props - The chart props, including minWidth constraining both the chart and its skeleton.
 * @param ref - Forwarded handle exposing the chart imperative API to the parent.
 * @returns The rendered heart rate chart with its skeleton placeholder and zoom panel.
 */
const ChartHR = forwardRef<ChartHRHandle, ChartHRProps>(
  (
    {
      keyRecoil,
      updatedSelectedStrip,
      idHtmlChart: idHtmlChartProp,
      dataChart,
      timezoneOffset,
      isOldUI: isOldUIProp,
      isDailyHrSummary,
      minWidth,
    },
    ref,
  ) => {
    const {
      isLoadingHrChart,
      isLoadingHrChartOptions,
      isRenderingChart,
      isOldUI,
      idHtmlChart,
      zoomScale,
      zoomIndex,
      setExtremesRef,
      handleResetZoom,
      handleZoomOut,
      handleZoomIn,
    } = useChartHR(
      {
        keyRecoil,
        updatedSelectedStrip,
        idHtmlChart: idHtmlChartProp,
        dataChart,
        timezoneOffset,
        isOldUI: isOldUIProp,
        isDailyHrSummary,
      },
      ref,
    );
    return (
      <div id="hr-chart" className={isOldUI ? 'hr-chart-wrapper-OLD' : 'hr-chart-wrapper'}>
        {(isLoadingHrChart || isLoadingHrChartOptions || isRenderingChart.current) && (
          <div
            style={{ minWidth: minWidth ? `${minWidth}px` : 'unset' }}
            className={classnames(
              isOldUI ? 'hr-chart-skeleton-container-OLD' : 'hr-chart-skeleton-container',
            )}
          >
            <div className="chart-container">
              <Skeleton height="100%" />
            </div>
          </div>
        )}
        <div
          id={idHtmlChart}
          style={{
            width: '100%',
            height: isOldUI ? 150 : 120,
            marginTop: 15,
            minWidth: minWidth ? `${minWidth}px` : 'unset',
          }}
        />
        {!(isLoadingHrChart || isLoadingHrChartOptions || isRenderingChart.current) && (
          <div className="zoom-panel-wrapper">
            <div className="zoom-panel">
              <IconButton
                id="zoom-in-btn"
                className="zoom-btn in"
                iconComponent={<img src={iconZoomOut} alt="zoom out" />}
                onClick={handleZoomOut}
              />
              <div className="zoom-scale">{`${zoomScale || ZOOM_SCALE_DEFAULT[zoomIndex]}%`}</div>
              <IconButton
                id="zoom-out-btn"
                className="zoom-btn out"
                iconComponent={<img src={iconZoomIn} alt="zoom in" />}
                onClick={handleZoomIn}
              />
            </div>
            {setExtremesRef.current.length > 0 && (
              <>
                <IconButton
                  id="resetZoomBtn"
                  className="reset-zoom-btn"
                  iconComponent={<img src={iconZoomReset} alt="zoom reset" />}
                  onClick={handleResetZoom}
                />
                <UncontrolledTooltip
                  className="custom-tooltip"
                  target="resetZoomBtn"
                  placement="bottom"
                  delay={{ show: 0, hide: 0 }}
                  hideArrow
                >
                  Reset zoom
                </UncontrolledTooltip>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default ChartHR;
